import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Card,
    Row,
    Col,
    Button,
    Table,
    Spin,
    DatePicker,
    Space,
    Select,
    Tag,
    Switch,
} from "antd";
import {
    SearchOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import bdmAnalyticsService from "../../services/bdmAnalyticsService";
import {
    ChevronUp,
    ChevronDown,
} from "lucide-react";

// Add CSS for table cells
const tableStyle = `
  .centered-table .ant-table-cell {
    text-align: center !important;
  }
  .centered-table .ant-table-cell:first-child {
    text-align: left !important;
  }
`;

const TASK_STATUS_MAP = {
    1: { label: "Unassigned", color: "default" },
    2: { label: "Assigned to Manager", color: "purple" },
    3: { label: "In Progress", color: "processing" },
    4: { label: "Completed (Quality Check)", color: "#10B981" }, // Custom green
    5: { label: "Delivered (Done)", color: "cyan" },
    6: { label: "Feedback", color: "warning" },
    7: { label: "Rejected", color: "error" },
    8: { label: "Cancelled", color: "error" },
    9: { label: "Failed", color: "error" },
    11: { label: "Cancelled by User", color: "error" },
};

const getRandomColor = (name) => {
    if (!name) return "#3b82f6";
    const colors = ["#ef4444", "#f97316", "#f59e0b", "#84cc16", "#10b981", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#d946ef", "#f43f5e"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const OpsUserAnalytics = () => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [viewTasks, setViewTasks] = useState(false);
    const [taskStatus, setTaskStatus] = useState("all");
    const [tasks, setTasks] = useState([]);

    // Helper function to format numbers
    const formatNumber = (num) => {
        if (!num && num !== 0) return "0";
        return Number(num).toLocaleString("en-IN");
    };

    const [expanded, setExpanded] = useState({}); // track expanded cards
    const [loadingExpand, setLoadingExpand] = useState({}); // track loading per card

    const sidebarCollapsed = useSelector((state) => state.ui.collapsed);

    // resize handling
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);


    // fetch analytics
    const fetchOpsAnalyticsData = async (focusUid = null) => {
        setLoading(true);
        const startDate = dateRange[0]
            ? dayjs(dateRange[0]).format("YYYY-MM-DD")
            : null;
        const endDate = dateRange[1]
            ? dayjs(dateRange[1]).format("YYYY-MM-DD")
            : null;

        const response = await bdmAnalyticsService.fetchOpsDashboard(
            startDate,
            endDate,
            null,
            focusUid,
            false,
            taskStatus === "all" ? null : taskStatus,
            viewTasks
        );

        if (response.success) {
            console.log(response.data);
            const data = response.data?.data || response.data;
            setAnalyticsData(data);
            // Safety check for tasks array
            setTasks(data.tasks || []);
        } else {
            console.error("Failed to fetch Ops Analytics data");
        }
        setLoading(false);
    };

    const handleSearch = () => {
        setExpanded({});
        fetchOpsAnalyticsData();
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
    };

    const isDateRangeSelected = () => {
        return dateRange && dateRange[0] !== null && dateRange[1] !== null;
    };

    useEffect(() => {
        // Fetch initially
        fetchOpsAnalyticsData();
    }, []);

    useEffect(() => {
        if (analyticsData) {
            fetchOpsAnalyticsData();
        }
    }, [viewTasks, taskStatus, dateRange]);

    const columns = [
        {
            title: "Metric",
            dataIndex: "metric",
            key: "metric",
            align: "left",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#F3F4F6",
                    fontWeight: 600,
                    color: "#161415",
                    textAlign: "left",
                },
            }),
        },
        {
            title: "Today",
            dataIndex: "today",
            key: "today",
            align: "center",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#F3F4F6",
                    fontWeight: 600,
                    color: "#161415",
                    textAlign: "center",
                },
            }),
        },
        {
            title: "Yesterday",
            dataIndex: "yesterday",
            key: "yesterday",
            align: "center",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#F3F4F6",
                    fontWeight: 600,
                    color: "#161415",
                    textAlign: "center",
                },
            }),
        },
        {
            title: "This Month",
            dataIndex: "month",
            key: "month",
            width: 140,
            align: "center",
            onHeaderCell: () => ({
                style: {
                    backgroundColor: "#F3F4F6",
                    fontWeight: 600,
                    color: "#161415",
                    textAlign: "center",
                },
            }),
        },
    ];

    const taskColumns = [
        {
            title: "Key",
            dataIndex: "key",
            key: "key",
            render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
        },
        {
            title: "Task Name",
            dataIndex: "title",
            key: "title",
            render: (text) => <span style={{ fontWeight: 500, width: "100px", }}>{text.slice(0, 50)}</span>,
        },
        {
            title: "Assigned To",
            dataIndex: "subscriber",
            key: "subscriber",
        },
        {
            title: "Status",
            dataIndex: "status_id",
            key: "status_id",
            render: (statusId) => {
                const status = TASK_STATUS_MAP[statusId] || { label: "Unknown", color: "default" };
                return (
                    <Tag color={status.color} style={{ borderRadius: "12px", padding: "0 10px" }}>
                        {status.label}
                    </Tag>
                );
            },
        },
        {
            title: "Words",
            dataIndex: "word_count",
            key: "word_count",
            render: (words) => formatNumber(words),
        },
        {
            title: "Deadline",
            dataIndex: "deadline",
            key: "deadline",
            render: (date) => dayjs(date).isValid() ? dayjs(date).format("MMM D, YYYY") : "-",
        },
        {
            title: "Created",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => dayjs(date).isValid() ? dayjs(date).format("MMM D, YYYY") : "-",
        },
    ];

    const CustomCard = ({
        title,
        value,
        icon,
    }) => {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#fff",
                    padding: "26px",
                    borderRadius: "8px",
                    minWidth: isMobile ? "80%" : sidebarCollapsed ? "240px" : "220px",
                    maxWidth: isMobile ? "90%" : sidebarCollapsed ? "240px" : "220px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    marginBottom: isMobile ? "15px" : "0",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                        alignItems: "flex-start",
                        flex: 1,
                        minWidth: 0,
                        marginRight: "12px",
                    }}
                >
                    <h4
                        style={{
                            fontSize: "13px",
                            color: "grey",
                            fontWeight: "400",
                            margin: 0,
                        }}
                    >
                        {title}
                    </h4>
                    <p
                        style={{
                            fontSize: "20px",
                            fontWeight: "600",
                            margin: 0,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                        }}
                    >
                        {value}
                    </p>
                </div>
                <div style={{ flexShrink: 0 }}>{icon}</div>
            </div>
        );
    };

    const toggleExpand = async (uid) => {
        if (expanded[uid]) {
            const newExpanded = { ...expanded };
            delete newExpanded[uid];
            setExpanded(newExpanded);
            return;
        }

        setLoadingExpand((prev) => ({ ...prev, [uid]: true }));

        const startDate = dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : null;
        const endDate = dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : null;

        try {
            const response = await bdmAnalyticsService.fetchOpsDashboard(
                startDate,
                endDate,
                null,
                uid, // focusUid
                true, // childrenOnly
                taskStatus === "all" ? null : taskStatus,
                false // View tasks usually disabled for hierarchy expansion
            );

            if (response.success) {
                setExpanded((prev) => ({
                    ...prev,
                    [uid]: response.data?.data || response.data,
                }));
            }
        } catch (err) {
            console.error("Failed to fetch team performance", err);
        }

        setLoadingExpand((prev) => {
            const newLoadingExpand = { ...prev };
            delete newLoadingExpand[uid];
            return newLoadingExpand;
        });
    };

    const renderSubTeam = (data, isNested = false) => {
        const teamMembers = Array.isArray(data) ? data : data?.hierarchy || [];
        if (teamMembers.length === 0) return null;

        return teamMembers.map((member) => (
            <Card
                key={member.uid}
                type="inner"
                hoverable={!isNested}
                style={{
                    marginTop: isNested ? 16 : 0,
                    marginBottom: isNested ? 0 : 0,
                    width: "100%",
                    minWidth: isNested ? "auto" : "340px",
                    maxWidth: isNested ? "100%" : "340px",
                    flex: isNested ? "none" : "0 0 auto",
                    borderRadius: "12px",
                    border: "1px solid #f0f0f0",
                    boxShadow: isNested ? "none" : "0 4px 12px rgba(0,0,0,0.05)",
                }}
                headStyle={{ borderBottom: "1px solid #f0f0f0", padding: "0 16px" }}
                bodyStyle={{ padding: "16px" }}
                title={
                    <div
                        style={{
                            padding: "12px 0px",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px"
                        }}
                    >
                        <div style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${getRandomColor(member.user || member.name)} 0%, #ffffff 100%)`,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "600",
                            fontSize: "16px",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.1)"
                        }}>
                            {(member.user || member.name || "U").charAt(0).toUpperCase()}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <h4 style={{ margin: 0, fontSize: "15px", fontWeight: "600", color: "#111827" }}>
                                {member.user || member.name}
                            </h4>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span style={{ color: "#6b7280", fontSize: "11px", background: "#f3f4f6", padding: "2px 6px", borderRadius: "4px" }}>
                                    {member.user_type || member.role}
                                </span>
                                {member?.member_count > 0 && (
                                    <span style={{ color: "#0ea5e9", fontSize: "11px", fontWeight: "500" }}>
                                        • {member?.member_count} Members
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                }
            >
                {isDateRangeSelected() ? (
                    <div>
                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                            <Col xs={12}>
                                <div style={{ background: "#eff6ff", padding: "10px", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
                                    <h4 style={{ margin: 0, fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Word Count</h4>
                                    <strong style={{ fontSize: "16px", fontWeight: 600, color: "#1d4ed8" }}>
                                        {formatNumber(member?.stats?.custom_range?.word_count || 0)}
                                    </strong>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
                                    <h4 style={{ margin: 0, fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Completed</h4>
                                    <strong style={{ fontSize: "16px", fontWeight: 600, color: "#15803d" }}>
                                        {formatNumber(member?.stats?.custom_range?.completed_tasks || 0)}
                                    </strong>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div style={{ background: "#fffbeb", padding: "10px", borderRadius: "8px", border: "1px solid #fde68a" }}>
                                    <h4 style={{ margin: 0, fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Feedbacks</h4>
                                    <strong style={{ fontSize: "16px", fontWeight: 600, color: "#b45309" }}>
                                        {formatNumber(member?.stats?.custom_range?.feedbacks || 0)}
                                    </strong>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <div style={{ background: "#fef2f2", padding: "10px", borderRadius: "8px", border: "1px solid #fecaca" }}>
                                    <h4 style={{ margin: 0, fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>Delayed</h4>
                                    <strong style={{ fontSize: "16px", fontWeight: 600, color: "#b91c1c" }}>
                                        {formatNumber(member?.stats?.custom_range?.delay_tasks || 0)}
                                    </strong>
                                </div>
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <Table
                        size="small"
                        pagination={false}
                        columns={columns}
                        className="centered-table"
                        dataSource={[
                            {
                                key: "1",
                                metric: <span style={{ fontSize: "13px", color: "#4b5563" }}>Word Count</span>,
                                today: <span style={{ fontWeight: 500 }}>{formatNumber(member?.stats?.today?.word_count || 0)}</span>,
                                yesterday: <span style={{ color: "#6b7280" }}>{formatNumber(member?.stats?.yesterday?.word_count || 0)}</span>,
                                month: <span style={{ fontWeight: 700, color: "#2563eb" }}>{formatNumber(member?.stats?.this_month?.word_count || 0)}</span>
                            },
                            {
                                key: "2",
                                metric: <span style={{ fontSize: "13px", color: "#4b5563" }}>Completed</span>,
                                today: <span style={{ fontWeight: 500 }}>{formatNumber(member?.stats?.today?.completed_tasks || 0)}</span>,
                                yesterday: <span style={{ color: "#6b7280" }}>{formatNumber(member?.stats?.yesterday?.completed_tasks || 0)}</span>,
                                month: <span style={{ fontWeight: 700, color: "#059669" }}>{formatNumber(member?.stats?.this_month?.completed_tasks || 0)}</span>
                            },
                            {
                                key: "3",
                                metric: <span style={{ fontSize: "13px", color: "#4b5563" }}>Feedbacks</span>,
                                today: <span style={{ fontWeight: 500 }}>{formatNumber(member?.stats?.today?.feedbacks || 0)}</span>,
                                yesterday: <span style={{ color: "#6b7280" }}>{formatNumber(member?.stats?.yesterday?.feedbacks || 0)}</span>,
                                month: <span style={{ fontWeight: 700, color: "#d97706" }}>{formatNumber(member?.stats?.this_month?.feedbacks || 0)}</span>
                            },
                            {
                                key: "4",
                                metric: <span style={{ fontSize: "13px", color: "#4b5563" }}>Delayed</span>,
                                today: <span style={{ fontWeight: 500, color: "#ef4444" }}>{formatNumber(member?.stats?.today?.delay_tasks || 0)}</span>,
                                yesterday: <span style={{ color: "#ef4444" }}>{formatNumber(member?.stats?.yesterday?.delay_tasks || 0)}</span>,
                                month: <span style={{ fontWeight: 700, color: "#dc2626" }}>{formatNumber(member?.stats?.this_month?.delay_tasks || 0)}</span>
                            },
                        ]}
                    />
                )}
                {(member.has_hierarchy) && (
                    <Button
                        type={expanded[member.uid] ? "default" : "dashed"}
                        block
                        style={{
                            marginTop: 12,
                            borderRadius: "6px",
                            borderColor: expanded[member.uid] ? "#d1d5db" : "#36c6d3",
                            color: expanded[member.uid] ? "#6b7280" : "#36c6d3",
                            background: expanded[member.uid] ? "#f9fafb" : "transparent",
                            fontWeight: "500",
                        }}
                        onClick={() => toggleExpand(member.uid)}
                        loading={loadingExpand[member.uid]}
                        disabled={loadingExpand[member.uid]}
                    >
                        {expanded[member.uid] ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                Hide Team <ChevronUp size={14} style={{ marginLeft: "6px" }} />
                            </div>
                        ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                View Performance <ChevronDown size={14} style={{ marginLeft: "6px" }} />
                            </div>
                        )}
                    </Button>
                )}
                {expanded[member.uid] && (
                    <div style={{ marginTop: 16 }}>
                        {loadingExpand[member.uid] ? <Spin /> : (
                            <div style={{ paddingLeft: "12px", borderLeft: "2px solid #e5e7eb", marginLeft: "4px" }}>
                                {renderSubTeam(expanded[member.uid], true)}
                            </div>
                        )}
                    </div>
                )}
            </Card>
        ));
    };

    if (loading)
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh", width: "100%" }}>
                <Spin />
            </div>
        );
    if (!analyticsData) return <p>No data available</p>;

    const { overall_stats, hierarchy } = analyticsData;
    const statsToShow = isDateRangeSelected() ? overall_stats?.custom_range || {} : overall_stats?.today || {};

    return (
        <div
            style={{
                // border: "1px solid red",
                width: "100%",
                overflowX: "auto",
                whiteSpace: "nowrap"
            }}>
            <style>{tableStyle}</style>

            {/* Filters */}
            <div
                style={{
                    background: "#ffffff",
                    padding: "20px",
                    borderRadius: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "20px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
            >
                <Space size={16} wrap>
                    <DatePicker.RangePicker
                        format="DD-MM-YYYY"
                        style={{ width: 280 }}
                        value={dateRange}
                        allowClear
                        onChange={handleDateRangeChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && dateRange[0] && dateRange[1]) {
                                handleSearch();
                            }
                        }}
                        placeholder={["Start Date", "End Date"]}
                    />
                    <Select
                        value={taskStatus}
                        style={{ width: 200 }}
                        onChange={setTaskStatus}
                        options={[
                            { value: "all", label: "All Status" },
                            ...Object.entries(TASK_STATUS_MAP).map(([key, value]) => ({
                                value: key,
                                label: value.label,
                            }))
                        ]}
                    />
                    <Space align="center">
                        <span style={{ fontSize: "14px" }}>View Tasks:</span>
                        <Switch checked={viewTasks} onChange={setViewTasks} />
                    </Space>
                </Space>

            </div>

            {/* Top KPIs */}
            <Row gutter={16} style={{ marginBottom: 32 }}>
                <div
                    style={{
                        display: "flex",
                        gap: "20px",
                        overflowX: "auto",
                        paddingBottom: "10px",
                        width: "100%",
                        flexWrap: "wrap"
                    }}
                >
                    <CustomCard
                        title={isDateRangeSelected() ? "Total Word Count" : "Word Count Today"}
                        value={formatNumber(statsToShow.word_count)}
                        icon={<FileTextOutlined style={{ fontSize: "24px", color: "#3b82f6" }} />}
                    />
                    <CustomCard
                        title={isDateRangeSelected() ? "Total Completed" : "Completed Today"}
                        value={formatNumber(statsToShow.completed_tasks)}
                        icon={<CheckCircleOutlined style={{ fontSize: "24px", color: "#22c55e" }} />}
                    />
                    <CustomCard
                        title={isDateRangeSelected() ? "Total Feedbacks" : "Feedbacks Today"}
                        value={formatNumber(statsToShow.feedbacks)}
                        icon={<WarningOutlined style={{ fontSize: "24px", color: "#d97706" }} />}
                    />
                    <CustomCard
                        title={isDateRangeSelected() ? "Total Delayed" : "Delayed Today"}
                        value={formatNumber(statsToShow.delay_tasks)}
                        icon={<ClockCircleOutlined style={{ fontSize: "24px", color: "#ef4444" }} />}
                    />
                </div>
            </Row>

            {/* Hierarchy Tree */}
            <div style={{ marginBottom: 40 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                    <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937", margin: 0 }}>
                        Business Heads Performance
                    </h3>
                    {hierarchy && (
                        <div style={{ background: "#36c6d3", color: "white", padding: "4px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600" }}>
                            Count : {Array.isArray(hierarchy) ? hierarchy.length : 1}
                        </div>
                    )}
                </div>

                {hierarchy && (
                    <div
                        style={{
                            display: "flex",
                            gap: "16px",
                            overflowX: "auto",
                            paddingBottom: "16px",
                            alignItems: "flex-start",
                            width: "100%",
                        }}
                    >
                        {Array.isArray(hierarchy) ? renderSubTeam(hierarchy) : renderSubTeam([hierarchy])}
                    </div>
                )}
            </div>

            {/* Task List Section */}
            {
                viewTasks && (
                    <div style={{ marginTop: 40, background: "#fff", padding: "24px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
                        <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "20px", color: "#1f2937", textAlign: "center" }}>
                            Task List
                        </h3>
                        <Table
                            dataSource={tasks}
                            columns={taskColumns}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                        />
                    </div>
                )
            }

        </div >
    );
};

export default OpsUserAnalytics;
